import type { api } from '@udm/contracts';
import {
  Marker,
  type GeoJSONSource,
  type GeoJSONSourceSpecification,
  type Map as MapLibreMap,
} from 'maplibre-gl';
import { cssVar } from './baseStyle';

/**
 * On-demand overlay: communes (OSM administrative areas) containing discovered devices outside every client area —
 * pink outlines + a label with the commune and its device count (DOM markers: the base style has no glyphs; there
 * are only a few dozen areas). Drawn below the device layers; removed completely when switched off.
 */
export const PROPOSALS = 'proposals';

export class ProposalsLayer {
  private markers: Marker[] = [];

  constructor(
    private readonly map: MapLibreMap,
    private readonly onPick: (osmRef: string) => void,
  ) {}

  show(areas: api.AdoptionProposal[]): void {
    const data = {
      type: 'FeatureCollection',
      features: areas.map((a) => ({ type: 'Feature', geometry: a.geometry, properties: { ref: a.osm_ref } })),
    } as unknown as GeoJSONSourceSpecification['data'];
    const src = this.map.getSource(PROPOSALS) as GeoJSONSource | undefined;
    if (src) src.setData(data);
    else {
      this.map.addSource(PROPOSALS, { type: 'geojson', data });
      const below = this.map.getLayer('devices-circles') ? 'devices-circles' : undefined;
      const pink = cssVar('--color-proposal');
      this.map.addLayer(
        {
          id: 'proposals-fill',
          type: 'fill',
          source: PROPOSALS,
          paint: { 'fill-color': pink, 'fill-opacity': 0.08 },
        },
        below,
      );
      this.map.addLayer(
        {
          id: 'proposals-line',
          type: 'line',
          source: PROPOSALS,
          paint: { 'line-color': pink, 'line-width': 2, 'line-dasharray': [3, 2] },
        },
        below,
      );
    }
    for (const m of this.markers) m.remove();
    this.markers = areas.map((a) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'proposal-label';
      el.textContent = `${a.name} · ${a.devices}`;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onPick(a.osm_ref);
      });
      return new Marker({ element: el }).setLngLat(a.label).addTo(this.map);
    });
  }

  hide(): void {
    for (const m of this.markers) m.remove();
    this.markers = [];
    for (const id of ['proposals-line', 'proposals-fill'])
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    if (this.map.getSource(PROPOSALS)) this.map.removeSource(PROPOSALS);
  }

  /** osm_ref of the proposal under a map click, if any. */
  hit(point: { x: number; y: number }): string | undefined {
    if (!this.map.getLayer('proposals-fill')) return undefined;
    return this.map.queryRenderedFeatures([point.x, point.y], { layers: ['proposals-fill'] })[0]?.properties
      ?.ref as string | undefined;
  }
}
